import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Seo } from "@/components/Layout";
import { PageHeader, Breadcrumbs } from "@/components/Bits";
import api from "@/lib/api";
import { BookOpen, Clock, Lock, ArrowRight, CheckCircle2 } from "lucide-react";

export function Courses() {
  const [course, setCourse] = useState(null);
  useEffect(() => { api.get("/courses/curso-a1-suiza").then(({ data }) => setCourse(data)); }, []);
  return (
    <>
      <Seo title="Cursos de alemán" description="Explora la ruta Alemán esencial para empezar en Suiza, nivel A1, con módulos y lecciones prácticas." canonicalPath="/cursos" />
      <PageHeader overline="Ruta de aprendizaje · A1" title="Alemán esencial para empezar en Suiza"
        subtitle="Diez módulos con situaciones reales. Empieza por el principio o salta al que más necesites." />
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-12">
        <Breadcrumbs items={[{ label: "Inicio", to: "/" }, { label: "Cursos" }]} />
        <div className="max-w-3xl mb-8 space-y-3 text-[15px] leading-relaxed text-muted-foreground">
          <p>Esta ruta de nivel A1 está diseñada para darte, cuanto antes, el alemán que de verdad usarás en Suiza: saludar, presentarte, moverte en transporte público, hacer la compra, entender a tu médico o resolver trámites básicos. En lugar de listas interminables de vocabulario, cada módulo parte de una situación real y te da las frases y estructuras que necesitas para resolverla.</p>
          <p>Los diez módulos siguen un orden progresivo, pero puedes empezar por el que más te urja. Cada lección incluye explicaciones en español, ejemplos en alemán estándar (la base que se enseña en Suiza) y ejercicios autoevaluables para fijar lo aprendido.</p>
        </div>
        {course && (
          <div className="grid gap-4">
            {course.modules.map((m) => (
              <div key={m.slug} className="rounded-lg border border-border bg-card overflow-hidden" data-testid={`module-${m.slug}`}>
                <div className="p-6 flex items-start gap-4">
                  <div className="h-11 w-11 shrink-0 rounded-md bg-primary/10 flex items-center justify-center font-display font-bold text-primary">{m.order}</div>
                  <div className="flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <h2 className="font-display font-semibold text-lg">{m.title}</h2>
                      {m.status === "coming_soon" && <span className="text-xs px-2 py-0.5 rounded-full bg-secondary text-muted-foreground flex items-center gap-1"><Lock size={11} />Próximamente</span>}
                    </div>
                    <p className="text-sm text-muted-foreground mt-1">{m.summary}</p>
                    {m.lessons.length > 0 && (
                      <div className="mt-4 grid sm:grid-cols-2 gap-2">
                        {m.lessons.map((l) => (
                          <Link key={l.slug} to={`/lecciones/${l.slug}`} data-testid={`lesson-link-${l.slug}`}
                            className="flex items-center justify-between rounded-md border border-border px-4 py-3 hover:border-primary/50 hover:bg-secondary transition-colors">
                            <span className="text-sm font-medium flex items-center gap-2"><BookOpen size={14} className="text-accent" />{l.title}</span>
                            <span className="text-xs text-muted-foreground flex items-center gap-1"><Clock size={12} />{l.duration_min}′</span>
                          </Link>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </>
  );
}

export default function LearnGerman() {
  return (
    <>
      <Seo title="Aprender alemán" description="Cómo aprender alemán para Suiza: rutas, niveles, método y diferencias entre alemán estándar y suizo." />
      <PageHeader overline="Aprender alemán" title="Un método pensado para hispanohablantes en Suiza"
        subtitle="Explicaciones claras en español, alemán estándar como base y una introducción progresiva al alemán suizo." />
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-12 grid lg:grid-cols-3 gap-6">
        {[
          { t: "Rutas y niveles", d: "Empieza en A1 con la ruta esencial para tus primeras semanas.", to: "/cursos", cta: "Ver cursos" },
          { t: "Prueba de nivel", d: "Descubre tu punto de partida orientativo en pocos minutos.", to: "/prueba-de-nivel", cta: "Hacer la prueba" },
          { t: "Herramientas", d: "Practica artículos, orden de palabras y vocabulario gratis.", to: "/herramientas", cta: "Explorar" },
        ].map((c) => (
          <div key={c.t} className="rounded-lg border border-border bg-card p-7">
            <CheckCircle2 size={22} className="text-primary mb-4" />
            <h2 className="font-display font-semibold text-xl">{c.t}</h2>
            <p className="mt-2 text-sm text-muted-foreground leading-relaxed">{c.d}</p>
            <Link to={c.to}><Button variant="outline" className="mt-5 rounded-full">{c.cta} <ArrowRight size={14} className="ml-1" /></Button></Link>
          </div>
        ))}
      </div>
    </>
  );
}
